// /home/ubuntu/melyn_cm_platform/frontend/src/pages/Settings.js
import React, { useState, useEffect, useCallback } from 'react';
import './Settings.css'; // Create this CSS file later
import { FaUser, FaFileInvoiceDollar, FaBell, FaLock, FaCog, FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff } from "react-icons/fa";
// import { IoClose } from "react-icons/io5"; // Unused

import InvoiceHistory from "../components/InvoiceHistory";
import apiClient from '../apiClient';
import ConfirmationModal from '../components/DeleteConfirmationModal';
import { useNavigate } from 'react-router-dom';

const settingsSections = [
  { id: "profile", name: "Profile", icon: FaUser },
  { id: "subscription", name: "Subscription", icon: FaFileInvoiceDollar },
  { id: "invoices", name: "Invoices", icon: FaFileInvoiceDollar },
  { id: "notifications", name: "Notifications", icon: FaBell },
  { id: "security", name: "Security", icon: FaLock },
];

const adminSections = [
  { id: "plan-management", name: "Plan Management", icon: FaCog },
];

export default function Settings() {
  const [activeSection, setActiveSection] = useState("profile");
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    role: "",
    phone: "",
    title: "",
    department: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Plan management states
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planFormData, setPlanFormData] = useState({
    name: '',
    price_monthly: '',
    limit_projects: '',
    limit_employees: '',
    limit_stakeholders: '',
    limit_stakeholder_groups: '',
    limit_key_message_themes: '',
    limit_key_messages: '',
    is_active: true
  });

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: 'Cancel',
    type: 'danger',
    onConfirm: null,
    isLoading: false
  });

  // Helper functions for confirmation modal
  const openConfirmationModal = ({ title, message, confirmText, type = 'danger', onConfirm }) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      confirmText,
      cancelText: 'Cancel',
      type,
      onConfirm,
      isLoading: false
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
  };

  const setConfirmationLoading = (isLoading) => {
    setConfirmationModal(prev => ({ ...prev, isLoading }));
  };

  // Fetch employees on component mount
  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    // Initialize form with existing user data or defaults
    const userData = JSON.parse(localStorage.getItem("ezocmUser") || "{}");
    console.log("[DEBUG] Settings - User data from localStorage:", userData);
    setUser(userData);
    setFormData({
      name: userData.name || "",
      email: userData.email || "",
      organization: userData.organization || "",
      role: userData.role || "",
      phone: userData.phone || "",
      title: userData.title || "",
      department: userData.department || "",
    });
    
    // Also try to get fresh user data from API if logged in
    const token = localStorage.getItem('access_token');
    if (token) {
      apiClient.get('/auth/me')
      .then(response => {
        console.log("[DEBUG] Settings - Fresh user data from API:", response.data);
        setUser(response.data);
        localStorage.setItem("ezocmUser", JSON.stringify(response.data));
      })
      .catch(error => {
        console.log("[DEBUG] Settings - Failed to get fresh user data:", error);
      });
    }
  }, []);

  const isAdmin = user?.role === 'Admin' || user?.role === 'System Administrator';

  const loadPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      console.log('🔄 Loading plans via apiClient (Settings.js)...');
      const response = await apiClient.get('/auth/admin/plans');
      console.log('✅ Plans loaded successfully (Settings.js):', response.data);
      setPlans(response.data);
    } catch (error) {
      console.error('❌ Failed to load plans (Settings.js):', error);
      setError('Failed to load plans');
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  const loadSubscriptions = useCallback(async () => {
    setLoadingSubscriptions(true);
    try {
      console.log('🔄 Loading subscriptions via apiClient (Settings.js)...');
      const response = await apiClient.get('/auth/admin/subscriptions');
      console.log('✅ Subscriptions loaded successfully (Settings.js):', response.data);
      setSubscriptions(response.data);
    } catch (error) {
      console.error('❌ Failed to load subscriptions (Settings.js):', error);
      setError('Failed to load subscriptions');
    } finally {
      setLoadingSubscriptions(false);
    }
  }, []);

  const loadPlansAndSubscriptions = useCallback(async () => {
    await Promise.all([loadPlans(), loadSubscriptions()]);
  }, [loadPlans, loadSubscriptions]);

  useEffect(() => {
    const isAdminUser = user?.role === 'Admin' || user?.role === 'System Administrator';
    console.log("[DEBUG] Active section:", activeSection, "User role:", user?.role, "Is Admin:", isAdminUser);
    if (activeSection === 'plan-management' && isAdminUser) {
      console.log("[DEBUG] Loading plans and subscriptions for admin");
      loadPlansAndSubscriptions();
    }
  }, [activeSection, user, loadPlansAndSubscriptions]);

  const allSections = isAdmin ? [...settingsSections, ...adminSections] : settingsSections;
  
  console.log("[DEBUG] Settings render - User:", user, "Is Admin:", isAdmin, "All sections:", allSections.map(s => s.name));

  const handleCreatePlan = () => {
    setEditingPlan(null);
    setPlanFormData({
      name: '',
      price_monthly: '',
      limit_projects: '',
      limit_employees: '',
      limit_stakeholders: '',
      limit_stakeholder_groups: '',
      limit_key_message_themes: '',
      limit_key_messages: '',
      is_active: true
    });
    setShowPlanModal(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanFormData({
      name: plan.name,
      price_monthly: plan.price_monthly,
      limit_projects: plan.limit_projects || '',
      limit_employees: plan.limit_employees || '',
      limit_stakeholders: plan.limit_stakeholders || '',
      limit_stakeholder_groups: plan.limit_stakeholder_groups || '',
      limit_key_message_themes: plan.limit_key_message_themes || '',
      limit_key_messages: plan.limit_key_messages || '',
      is_active: plan.is_active
    });
    setShowPlanModal(true);
  };

  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...planFormData,
        price_monthly: parseFloat(planFormData.price_monthly) || 0,
        limit_projects: planFormData.limit_projects ? parseInt(planFormData.limit_projects) : null,
        limit_employees: planFormData.limit_employees ? parseInt(planFormData.limit_employees) : null,
        limit_stakeholders: planFormData.limit_stakeholders ? parseInt(planFormData.limit_stakeholders) : null,
        limit_stakeholder_groups: planFormData.limit_stakeholder_groups ? parseInt(planFormData.limit_stakeholder_groups) : null,
        limit_key_message_themes: planFormData.limit_key_message_themes ? parseInt(planFormData.limit_key_message_themes) : null,
        limit_key_messages: planFormData.limit_key_messages ? parseInt(planFormData.limit_key_messages) : null,
      };

      if (editingPlan) {
        await apiClient.put(`/auth/admin/plans/${editingPlan.id}`, payload);
        setSuccessMessage('Plan updated successfully');
      } else {
        await apiClient.post('/auth/admin/plans', payload);
        setSuccessMessage('Plan created successfully');
      }

      setShowPlanModal(false);
      await loadPlans();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save plan:', error);
      setError(error.response?.data?.error || 'Failed to save plan');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleTogglePlan = async (planId) => {
    try {
      await apiClient.put(`/auth/admin/plans/${planId}/toggle`, {});
      setSuccessMessage('Plan status updated');
      await loadPlans();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Failed to toggle plan:', error);
      setError('Failed to update plan status');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeletePlan = (planId, planName) => {
    openConfirmationModal({
      title: 'Delete Plan',
      message: `Are you sure you want to delete the "${planName}" plan? This action cannot be undone.`,
      confirmText: 'Delete Plan',
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await apiClient.delete(`/auth/admin/plans/${planId}`);
          setSuccessMessage('Plan deleted successfully');
          await loadPlans();
          setTimeout(() => setSuccessMessage(''), 3000);
          closeConfirmationModal();
        } catch (error) {
          console.error('Failed to delete plan:', error);
          setError(error.response?.data?.error || 'Failed to delete plan');
          setTimeout(() => setError(''), 5000);
          closeConfirmationModal();
        }
      }
    });
  };

  const handleChangeUserPlan = async (userId, newPlanId) => {
    try {
      await apiClient.put(`/auth/admin/users/${userId}/subscription`, 
        { plan_definition_id: newPlanId }
      );
      setSuccessMessage('User plan updated successfully');
      await loadSubscriptions();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Failed to change user plan:', error);
      setError(error.response?.data?.error || 'Failed to change user plan');
      setTimeout(() => setError(''), 5000);
    }
  };

  const fetchEmployees = () => {
    setLoadingEmployees(true);
    setError(null);
    console.log('🔄 Loading employees via apiClient (Settings.js)...');
    apiClient.get('/employees')
      .then(response => {
        console.log('✅ Employees loaded successfully (Settings.js):', response.data);
        setEmployees(response.data.employees);
      })
      .catch(err => {
        console.error('❌ Error fetching employees (Settings.js):', err);
        setError('Failed to load employees. Please try again.');
      })
      .finally(() => {
        setLoadingEmployees(false);
      });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Save to localStorage (replace with API call in production)
    localStorage.setItem("ezocmUser", JSON.stringify({ ...user, ...formData }));
    setSuccessMessage("Profile updated successfully!");
    setIsEditing(false);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    setSuccessMessage(null); // Clear previous messages
    setError(null);
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccessMessage(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    apiClient.post('/employees/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(response => {
        setSuccessMessage(response.data.message || 'File uploaded successfully!');
        setSelectedFile(null); // Clear file input
        document.getElementById('employee-file-input').value = ''; // Reset file input visually
        fetchEmployees(); // Refresh the employee list
      })
      .catch(err => {
        console.error('Error uploading file:', err);
        const errorMsg = err.response?.data?.error || 'File upload failed.';
        const errorDetails = err.response?.data?.details;
        if (errorDetails && Array.isArray(errorDetails)) {
          setError(`${errorMsg}: ${errorDetails.join(' ')}`);
        } else {
          setError(errorMsg);
        }
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  // Placeholder functions for Edit/Delete - Implement later
  const handleEditEmployee = (employeeId) => {
    console.log(`Edit employee ${employeeId}`);
    // TODO: Implement edit functionality (e.g., open modal)
    setError('Edit functionality not yet implemented.');
    setTimeout(() => setError(null), 3000);
  };

  const handleDeleteEmployee = (employeeId, employeeName) => {
    console.log(`Delete employee ${employeeId}`);
    openConfirmationModal({
      title: 'Delete Employee',
      message: `Are you sure you want to delete employee "${employeeName}"? This action cannot be undone.`,
      confirmText: 'Delete Employee',
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          const response = await apiClient.delete(`/employees/${employeeId}`);
          setSuccessMessage(response.data.message || 'Employee deleted successfully!');
          fetchEmployees(); // Refresh list
          setTimeout(() => setSuccessMessage(null), 3000);
          closeConfirmationModal();
        } catch (err) {
          console.error('Error deleting employee:', err);
          setError(err.response?.data?.error || 'Failed to delete employee.');
          setTimeout(() => setError(null), 5000);
          closeConfirmationModal();
        }
      }
    });
  };

  // If the main `loading` state was used for a general page loading indicator:
  // if (loading) { // This `loading` state was removed
  //   return <div className="settings-loading">Loading settings...</div>;
  // }
  // This should be replaced by a check on more specific loading states if needed, 
  // or removed if not applicable for this page structure.

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Temporary Debug Section */}
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="font-bold text-yellow-800">Debug Info:</h4>
          <p className="text-yellow-700">User Role: <strong>{user?.role || 'Not loaded'}</strong></p>
          <p className="text-yellow-700">Is Admin: <strong>{isAdmin ? 'Yes' : 'No'}</strong></p>
          <p className="text-yellow-700">Available Sections: <strong>{allSections.map(s => s.name).join(', ')}</strong></p>
          <p className="text-yellow-700">User Email: <strong>{user?.email || 'Not loaded'}</strong></p>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Settings Sidebar */}
          <div className="w-full md:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Settings</h2>
              <nav className="space-y-1">
                {allSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      activeSection === section.id
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <section.icon className="w-5 h-5" />
                    {section.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeSection === "profile" && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">Profile Information</h3>
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
                    >
                      Edit Profile
                    </button>
                  )}
                </div>

                {successMessage && (
                  <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-lg">
                    {successMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization
                      </label>
                      <input
                        type="text"
                        name="organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <input
                        type="text"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Job Title
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Department
                      </label>
                      <input
                        type="text"
                        name="department"
                        value={formData.department}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>
                  </div>

                  {isEditing && (
                    <div className="flex justify-end gap-4">
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 rounded-lg btn-ezocm-gold"
                      >
                        Save Changes
                      </button>
                    </div>
                  )}
                </form>
              </div>
            )}



            {activeSection === "subscription" && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Subscription Management</h3>
                <p className="text-gray-600 mb-4">Manage your subscription plan and billing settings.</p>
                <button
                  onClick={() => navigate('/settings/subscription')}
                  className="px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
                >
                  Go to Subscription Management
                </button>
              </div>
            )}

            {activeSection === "invoices" && <InvoiceHistory />}

            {activeSection === "notifications" && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Notification Preferences</h3>
                {/* Notification settings will go here */}
              </div>
            )}

            {activeSection === "security" && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Security Settings</h3>
                {/* Security settings will go here */}
              </div>
            )}

            {/* Admin Plan Management Section */}
            {activeSection === "plan-management" && (
              isAdmin ? (
                <div className="space-y-6">
                  {/* Success/Error Messages */}
                  {successMessage && (
                    <div className="p-4 bg-green-50 text-green-700 rounded-lg">
                      {successMessage}
                    </div>
                  )}
                  {error && (
                    <div className="p-4 bg-red-50 text-red-700 rounded-lg">
                      {error}
                    </div>
                  )}

                  {/* Plan Definitions Management */}
                  <div className="bg-white rounded-2xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-semibold text-gray-900">Plan Definitions</h3>
                      <button
                        onClick={handleCreatePlan}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg btn-ezocm-gold"
                      >
                        <FaPlus className="w-4 h-4" />
                        Create Plan
                      </button>
                    </div>

                    {loadingPlans ? (
                      <div className="text-center py-8">Loading plans...</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                          <thead style={{backgroundColor: '#1992D4', color: '#FFFFFF'}} className="sticky top-0 z-10">
                            <tr className="border-b" style={{borderColor: '#1478B0'}}>
                              <th className="py-3 px-4 font-semibold">Name</th>
                              <th className="py-3 px-4 font-semibold">Price</th>
                              <th className="py-3 px-4 font-semibold">Projects</th>
                              <th className="py-3 px-4 font-semibold">Employees</th>
                              <th className="py-3 px-4 font-semibold">Stakeholders</th>
                              <th className="py-3 px-4 font-semibold">Groups</th>
                              <th className="py-3 px-4 font-semibold">Messages</th>
                              <th className="py-3 px-4 font-semibold">Status</th>
                              <th className="py-3 px-4 font-semibold">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {plans.map(plan => (
                              <tr key={plan.id} className="border-b">
                                <td className="py-3 px-4 font-medium">{plan.name}</td>
                                <td className="py-3 px-4">
                                  {parseFloat(plan.price_monthly) === 0 ? 'Free' : `$${plan.price_monthly}/month`}
                                </td>
                                <td className="py-3 px-4">{plan.limit_projects || 'Unlimited'}</td>
                                <td className="py-3 px-4">{plan.limit_employees || 'Unlimited'}</td>
                                <td className="py-3 px-4">{plan.limit_stakeholders || 'Unlimited'}</td>
                                <td className="py-3 px-4">{plan.limit_stakeholder_groups || 'Unlimited'}</td>
                                <td className="py-3 px-4">{plan.limit_key_messages || 'Unlimited'}</td>
                                <td className="py-3 px-4">
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    plan.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {plan.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleEditPlan(plan)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                      title="Edit Plan"
                                    >
                                      <FaEdit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleTogglePlan(plan.id)}
                                      className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                                      title={plan.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                      {plan.is_active ? <FaToggleOn className="w-4 h-4" /> : <FaToggleOff className="w-4 h-4" />}
                                    </button>
                                    <button
                                      onClick={() => handleDeletePlan(plan.id, plan.name)}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                                      title="Delete Plan"
                                    >
                                      <FaTrash className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* User Subscriptions Management */}
                  <div className="bg-white rounded-2xl shadow-sm p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">User Subscriptions</h3>
                    
                    {loadingSubscriptions ? (
                      <div className="text-center py-8">Loading subscriptions...</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                          <thead style={{backgroundColor: '#1992D4', color: '#FFFFFF'}} className="sticky top-0 z-10">
                            <tr className="border-b" style={{borderColor: '#1478B0'}}>
                              <th className="py-3 px-4 font-semibold">User</th>
                              <th className="py-3 px-4 font-semibold">Email</th>
                              <th className="py-3 px-4 font-semibold">Current Plan</th>
                              <th className="py-3 px-4 font-semibold">Status</th>
                              <th className="py-3 px-4 font-semibold">Start Date</th>
                              <th className="py-3 px-4 font-semibold">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subscriptions.map(subscription => (
                              <tr key={subscription.id} className="border-b">
                                <td className="py-3 px-4 font-medium">
                                  {subscription.employee?.name || 'Unknown User'}
                                </td>
                                <td className="py-3 px-4">
                                  {subscription.employee?.email || 'N/A'}
                                </td>
                                <td className="py-3 px-4">
                                  {subscription.plan_name || 'No Plan'}
                                </td>
                                <td className="py-3 px-4">
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    subscription.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {subscription.status}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  {subscription.start_date ? new Date(subscription.start_date).toLocaleDateString() : 'N/A'}
                                </td>
                                <td className="py-3 px-4">
                                  <select
                                    value={subscription.plan_definition_id}
                                    onChange={(e) => handleChangeUserPlan(subscription.employee_id, parseInt(e.target.value))}
                                    className="px-3 py-1 border border-gray-300 rounded text-sm"
                                  >
                                    {plans.filter(plan => plan.is_active).map(plan => (
                                      <option key={plan.id} value={plan.id}>
                                        {plan.name} - {parseFloat(plan.price_monthly) === 0 ? 'Free' : `$${plan.price_monthly}/month`}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Access Denied</h3>
                  <p className="text-gray-600">
                    You need administrator privileges to access plan management. 
                    Current role: <strong>{user?.role || 'Unknown'}</strong>
                  </p>
                  <p className="text-gray-600 mt-2">
                    Debug info: isAdmin = {isAdmin ? 'true' : 'false'}
                  </p>
                </div>
              )
            )}

            {/* Plan Creation/Edit Modal */}
            {showPlanModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">
                    {editingPlan ? 'Edit Plan' : 'Create New Plan'}
                  </h3>
                  
                  <form onSubmit={handlePlanSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Plan Name *
                      </label>
                      <input
                        type="text"
                        value={planFormData.name}
                        onChange={(e) => setPlanFormData({...planFormData, name: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Monthly Price ($) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={planFormData.price_monthly}
                        onChange={(e) => setPlanFormData({...planFormData, price_monthly: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Project Limit
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={planFormData.limit_projects}
                          onChange={(e) => setPlanFormData({...planFormData, limit_projects: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Unlimited"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Employee Limit
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={planFormData.limit_employees}
                          onChange={(e) => setPlanFormData({...planFormData, limit_employees: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Unlimited"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Stakeholder Limit
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={planFormData.limit_stakeholders}
                          onChange={(e) => setPlanFormData({...planFormData, limit_stakeholders: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Unlimited"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Stakeholder Group Limit
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={planFormData.limit_stakeholder_groups}
                          onChange={(e) => setPlanFormData({...planFormData, limit_stakeholder_groups: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Unlimited"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Theme Limit
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={planFormData.limit_key_message_themes}
                          onChange={(e) => setPlanFormData({...planFormData, limit_key_message_themes: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Unlimited"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Message Limit
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={planFormData.limit_key_messages}
                          onChange={(e) => setPlanFormData({...planFormData, limit_key_messages: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Unlimited"
                        />
                      </div>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={planFormData.is_active}
                        onChange={(e) => setPlanFormData({...planFormData, is_active: e.target.checked})}
                        className="mr-2"
                      />
                      <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                        Active Plan
                      </label>
                    </div>

                    <div className="flex justify-end gap-4 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowPlanModal(false)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 rounded-lg btn-ezocm-gold"
                      >
                        {editingPlan ? 'Update Plan' : 'Create Plan'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Section 1: Employee Management */}
            <div className="settings-section card">
              <h2>Settings: Employee Management</h2>
              <p>Upload, view, and manage employees for your organization.</p>

              {/* Upload Area */} 
              <div className="upload-area form-group">
                <label htmlFor="employee-file-input">Upload Employee Spreadsheet (.xlsx, .csv):</label>
                <div className="upload-controls">
                  <input
                    type="file"
                    id="employee-file-input"
                    accept=".xlsx, .csv"
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                  <button
                    className="action-button primary"
                    onClick={handleUpload}
                    disabled={!selectedFile || isUploading}
                  >
                    {isUploading ? 'Uploading...' : 'Upload File'}
                  </button>
                </div>
                <p className="form-text text-muted">
                  Required columns: Name, Email address, Department, Role.
                </p>
              </div>

              {/* Status Messages */} 
              {successMessage && <div className="success-message"><p>{successMessage}</p></div>}
              {error && <div className="error-message"><p>{error}</p></div>}

              {/* Employee Table */} 
              <div className="employee-table-container">
                <h4>Current Employees</h4>
                {loadingEmployees ? (
                  <p>Loading employees...</p>
                ) : employees.length === 0 && !error ? (
                  <p>No employees found. Upload a spreadsheet to add employees.</p>
                ) : (
                  <table className="settings-table">
                    <thead style={{backgroundColor: '#1992D4', color: '#FFFFFF'}} className="sticky top-0 z-10">
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Department</th>
                        <th>Role</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map(emp => (
                        <tr key={emp.id}>
                          <td>{emp.name}</td>
                          <td>{emp.email}</td>
                          <td>{emp.department || 'N/A'}</td>
                          <td>{emp.role || 'N/A'}</td>
                          <td className="action-buttons">
                            <button
                              className="icon-button edit-button"
                              onClick={() => handleEditEmployee(emp.id)}
                              title="Edit Employee"
                            >
                              {/* Add Edit Icon later */}
                              Edit
                            </button>
                            <button
                              className="icon-button delete-button"
                              onClick={() => handleDeleteEmployee(emp.id, emp.name)}
                              title="Delete Employee"
                            >
                              {/* Add Delete Icon later */}
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Section 2: Group Management */}
            <div className="settings-section card">
              <h2>Settings: Group Management</h2>
              <p>Create, view, and manage stakeholder groups for your organization.</p>
              {/* Add your group management UI here */}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        type={confirmationModal.type}
        onConfirm={confirmationModal.onConfirm}
        onClose={closeConfirmationModal}
        isLoading={confirmationModal.isLoading}
      />
    </div>
  );
}

